// Shared expense-type helpers.
//
// `expenses.type` is lookup-driven (migration 0023 → `lookup_options.category =
// 'expense_type'`, seeded capitalized: Travel / Equipment / Meals / Lodging /
// Software / Other). Rows written before that migration stored the lowercase
// enum, so display and travel-detection both have to tolerate either casing.
//
// These lived as identical private copies in ExpensesPage and
// ExpenseDetailModal. Shared display helpers belong in one place (same reason
// `formatMoney` lives in utils/money.ts) — a third copy in the travel modal
// would have been the point where they drifted apart.

/** Display label for legacy lowercase `type` codes. Lookup-managed values
 *  (which are already human-readable) fall through unchanged. */
export const TYPE_LABEL: Record<string, string> = {
  travel: 'Travel',
  equipment: 'Equipment',
  meals: 'Meals',
  lodging: 'Lodging',
  software: 'Software',
  other: 'Other',
};

/** Human label for an expense type — the lookup value unless it's a legacy code. */
export function expenseTypeLabel(type: string): string {
  return TYPE_LABEL[type] ?? type;
}

/** The expense type that records odometer miles (migration 0052). */
export const MILEAGE_TYPE = 'Travel (track mileage)';

/**
 * Whether a type carries a Miles field.
 *
 * Was every 'Travel' expense, which put a Miles box on parking, tolls and
 * flights — none of which have mileage — so it was a field to skip past on most
 * travel rows. Now only the explicit mileage type does.
 *
 * Plain 'Travel' (and the legacy lowercase 'travel') deliberately return false.
 * Rows recorded before the split keep their stored mileage, and every surface
 * that can edit one shows the field when a value is already there — see
 * ExpenseDetailModal. Hiding data that exists would be worse than an extra box.
 */
export function tracksMileage(t: string): boolean {
  return t.trim().toLowerCase() === MILEAGE_TYPE.toLowerCase();
}
